import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { login, clearError, setAuth } from '../store/slices/authSlice';
import axios from 'axios';
import { useDomainParams } from '../hooks/useDomainParams';

const API_BASE = 'http://localhost:3001/api/v1/auth';

const socialProviders = [
  { name: 'google', label: 'Google', icon: 'G', color: '#DB4437' },
  { name: 'facebook', label: 'Facebook', icon: 'f', color: '#4267B2' },
  { name: 'instagram', label: 'Instagram', icon: '📷', color: '#E4405F' },
  { name: 'linkedin', label: 'LinkedIn', icon: 'in', color: '#0A66C2' },
  { name: 'whatsapp', label: 'WhatsApp', icon: '💬', color: '#25D366' }
];

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.auth);

  const searchParams = useSearchParams()[0];
  const verified = searchParams.get('verified');
  const registered = searchParams.get('registered');

  const { branding, isLoading: domainLoading, error: domainError } = useDomainParams();
  const primaryColor = branding?.primaryColor || '#667eea';
  const logoUrl = branding?.logo;
  const appTitle = branding?.title || 'Welcome';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refresh = params.get('refresh');
    if (token && refresh) {
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refresh);
      dispatch(setAuth({ token, refreshToken: refresh }));
      window.location.href = '/';
    }
  }, [dispatch]);

  useEffect(() => {
    if (verified === 'true') {
      alert('Email verified successfully! Please login.');
    }
    if (registered === 'true') {
      alert('Registration successful! Please login with your credentials.');
    }
  }, [verified, registered]);

  if (domainError) {
    return (
      <div className="login-page" style={{ ...styles.page, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}>
        <div className="login-card" style={styles.card}>
          <div style={styles.errorBanner}>
            <span style={styles.errorIcon}>⚠️</span>
            <h2 style={styles.errorTitle}>Invalid Application Domain Configuration</h2>
            <p style={styles.errorText}>{domainError}</p>
            <a href="/" style={styles.homeLink}>← Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login({ email, password }));
  };

  const handleSocialLogin = async (provider) => {
    try {
      const response = await axios.get(`${API_BASE}/social/${provider}`);
      if (response.data.success && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      } else if (response.data.message) {
        alert(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login not configured: ${response.data.message}\n\nTo enable, add credentials to services/auth/.env`);
      }
    } catch (err) {
      console.error('Social login error:', err);
      if (err.response?.data?.message) {
        alert(`Error: ${err.response.data.message}`);
      } else {
        alert('Social login failed. Make sure auth service is running on port 3001.');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      alert('Please enter your email first');
      return;
    }
    try {
      await axios.post(`${API_BASE}/send-verification`, { email });
      setVerificationSent(true);
      setTimeout(() => setVerificationSent(false), 3000);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send verification email');
    }
  };

  return (
    <div className="login-page" style={{ ...styles.page, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          {logoUrl ? (
            <img src={logoUrl} alt={appTitle} style={styles.logo} />
          ) : (
            <div style={styles.logoIcon}>👤</div>
          )}
          <h2 style={styles.title}>{appTitle}</h2>
        </div>

        {error && (
          <div style={styles.error}>
            {error}
            {error.includes('verify') && (
              <div style={styles.verificationOption}>
                <button onClick={handleResendVerification} style={styles.resendBtn}>
                  {verificationSent ? 'Email Sent!' : 'Resend Verification Email'}
                </button>
              </div>
            )}
          </div>
        )}

        <div style={styles.socialSection}>
          <p style={styles.socialLabel}>Login with Social</p>
          <div style={styles.socialButtons}>
            {socialProviders.map((provider) => (
              <button
                key={provider.name}
                type="button"
                onClick={() => handleSocialLogin(provider.name)}
                style={{ ...styles.socialBtn, borderColor: provider.color }}
              >
                <span style={styles.socialIcon}>{provider.icon}</span>
                <span>{provider.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={styles.divider}>
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <button type="submit" disabled={isLoading || domainLoading} style={{ ...styles.submitBtn, background: primaryColor }}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <p style={styles.registerLink}>
          Don't have an account? <a href="/register" style={{ color: primaryColor }}>Register</a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: 'white',
    padding: '40px',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '420px',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '12px',
    margin: '0 auto 12px auto',
    display: 'block',
  },
  logoIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  verificationOption: {
    marginTop: '10px',
  },
  resendBtn: {
    background: '#667eea',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  socialSection: {
    marginBottom: '20px',
  },
  socialLabel: {
    textAlign: 'center',
    color: '#888',
    marginBottom: '15px',
    fontSize: '14px',
  },
  socialButtons: {

    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    justifyContent: 'center',
  },
  socialBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    background: '#667eea',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s',
    flex: '1',
    minWidth: '140px',
    justifyContent: 'center',
  },
  socialIcon: {
    fontSize: '18px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    color: '#888',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  registerLink: {
    textAlign: 'center',
    marginTop: '20px',
    color: '#666',
    fontSize: '14px',
  },
  errorBanner: {
    textAlign: 'center',
    padding: '20px',
  },
  errorIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '8px',
  },
  errorText: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '20px',
  },
  homeLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
  },
};

export default Login;